import classNames from 'classnames';
import * as Stars from '@/assets/graphics/stars/stars';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import styles from './star-rating.module.scss';

export interface StarRatingProps {
  /** The class name to apply */
  className?: string;
  /** The rating value */
  rating: number;
}

const LENGTH = 5;

const StarRating = ({ className, rating }: StarRatingProps) => {
  const { brand } = usePageDetailsContext();
  const { t } = useI18n();

  const renderStar = (index: number) => {
    const isFullStar = rating >= index + 1;
    const isHalfStar = !isFullStar && rating > index;

    const FilledComponent = Stars[`${capitalizeFirstLetter(`${brand}`)}Filled`];
    const HalfFilledComponent =
      Stars[`${capitalizeFirstLetter(`${brand}`)}HalfFilled`];

    if (!FilledComponent || !HalfFilledComponent) {
      return null;
    }

    return (
      <div key={index} className={styles.star}>
        {isFullStar && (
          <FilledComponent
            height={24}
            preserveAspectRatio="xMidYMid meet"
            width={24}
          />
        )}

        {isHalfStar && (
          <HalfFilledComponent
            className={styles.halfStar}
            height={24}
            preserveAspectRatio="xMidYMid meet"
            width={24}
          />
        )}
      </div>
    );
  };

  return (
    <div
      aria-label={t('items.search.productCard.rating', {
        LENGTH,
        rating,
      })}
      className={classNames(styles.component, className)}
      role="img"
    >
      {Array.from({ length: LENGTH }, (_, index) => renderStar(index))}
    </div>
  );
};

StarRating.displayName = 'StarRating';

export default StarRating;
